import { useReducer } from 'react';
import { Accordion } from '@packages/ui/composites';
import { Section } from '@packages/ui/layout';
import type { ResolvedAccordionBlock, RenderBlockFn, BlockRendererSharedProps } from '../types/blocks';

export interface AccordionBlockRendererProps extends BlockRendererSharedProps {
  block: ResolvedAccordionBlock;
  renderBlock: RenderBlockFn;
}

interface AccordionState {
  openIds: string[];
  visited: Set<string>;
}

type AccordionAction =
  | { type: 'TOGGLE'; id: string; accordionType: 'single' | 'multiple' }
  | { type: 'SET_VALUE'; value: string | string[]; accordionType: 'single' | 'multiple' };

function accordionReducer(state: AccordionState, action: AccordionAction): AccordionState {
  switch (action.type) {
    case 'SET_VALUE': {
      const nextIds = Array.isArray(action.value)
        ? action.value
        : action.value ? [action.value] : [];
      const nextVisited = new Set(state.visited);
      for (const id of nextIds) nextVisited.add(id);
      return { openIds: nextIds, visited: nextVisited };
    }
    case 'TOGGLE': {
      const isOpen = state.openIds.includes(action.id);
      let nextIds: string[];
      if (action.accordionType === 'single') {
        nextIds = isOpen ? [] : [action.id];
      } else {
        nextIds = isOpen
          ? state.openIds.filter((x) => x !== action.id)
          : [...state.openIds, action.id];
      }
      const nextVisited = new Set(state.visited);
      if (!isOpen) nextVisited.add(action.id);
      return { openIds: nextIds, visited: nextVisited };
    }
  }
}

export function AccordionBlockRenderer({
  block,
  renderBlock,
  params,
  meta,
  onSuccess,
  onError,
  onCancel,
  inheritedDataSource,
  handle,
}: AccordionBlockRendererProps): JSX.Element {
  const defaultOpen = block.sections[0]?.id ?? '';
  const accordionType = block.accordionType ?? 'single';
  const shared: BlockRendererSharedProps = {
    params,
    meta,
    onSuccess,
    onError,
    onCancel,
    inheritedDataSource,
    handle,
  };

  const [state, dispatch] = useReducer(accordionReducer, {
    openIds: defaultOpen ? [defaultOpen] : [],
    visited: new Set<string>(defaultOpen ? [defaultOpen] : []),
  });

  const value = accordionType === 'single'
    ? (state.openIds[0] ?? '')
    : state.openIds;

  return (
    <Section title={block.title} semanticId={block.semanticId}>
      <Accordion.Root
        type={accordionType}
        value={value}
        grouped={block.grouped}
        semanticId={block.semanticId ?? 'accordionBlock'}
        onValueChange={(next) =>
          dispatch({ type: 'SET_VALUE', value: next, accordionType })
        }
      >
        {block.sections.map((section) => (
          <Accordion.Item key={section.id} value={section.id}>
            <Accordion.Trigger>{section.label}</Accordion.Trigger>
            <Accordion.Content>
              {state.visited.has(section.id)
                ? renderBlock(section.block, shared)
                : null}
            </Accordion.Content>
          </Accordion.Item>
        ))}
      </Accordion.Root>
    </Section>
  );
}
